import angular from 'angular';
import uiRouter from '@uirouter/angularjs';
import moment from 'angular-moment';
import duScroll from 'angular-scroll';

import routing from './event_report.routes';

import EventService from '../../components/api/event/event.service';
import CsvUtil from '../../components/logic/csv_util/csv.util';

import chartConfig from './event_report.config';

export class EventReportController {
  /*@ngInject*/
  constructor($stateParams, $q, $filter, $interval, $document, $timeout, moment, EventService, CsvUtil) {
    this.id = $stateParams.id;
    this.$q = $q;
    this.$filter = $filter;
    this.$interval = $interval;
    this.$document = $document;
    this.$timeout = $timeout;
    this.moment = moment;
    this.EventService = EventService;
    this.CsvUtil = CsvUtil;

    this.downloadFileName = 'events.csv';

    this.scrollElement = angular.element(document.querySelector('#event-report-content'));
    this.scrollItemSize = 321;
    this.loadThreshold = this.scrollItemSize * 7;

    this.autoScrolling = false;
    this.dragging = false;

    this.currentDate = this.moment().startOf('day').toDate();
    this.targetDate = this.moment().startOf('day').toDate();
    this.events = [];

    this.scrollElement.on('scroll', () => {
      if (this.autoScrolling) {
        return;
      }

      //日付切り替え用の処理
      const position = this.scrollElement.scrollLeft() - this.scrollItemSize / 2;
      const index = Math.floor(position / this.scrollItemSize);
      const targetDate = this.events[index + 1].date;

      if (this.targetDate.valueOf() !== targetDate.valueOf()) {
        this.$timeout(() => {
          this.targetDate = targetDate.toDate();
          this.setPlotLine();
        });
        this.currentDate = targetDate.toDate();
      }

      //無限スクロール用の処理
      this.infiniteScroll();
    });

    this.chartConfig = chartConfig(this);
  }

  $onInit() {
    const targetDate = this.moment(this.targetDate);
    this.loadEvent(targetDate).then(events => {
      this.events = events;
      this.scrollToDay(targetDate, 100);

      this.updateGraph(this.events);
      this.csvData = this.CsvUtil.createEventReportCsv(this.events);
      this.downloadFileName = targetDate.format('[event]_YYYY-MM[.csv]');
    });
  }

  loadEvent(date) {
    return new Promise((resolve, reject) => {
      this.EventService.api.show(
        {id: this.id, year: date.year(), month: date.month() + 1},
        events => {
          resolve(this.divideEvents(events, date));
        }
      )
    });
  }

  isSunday(date) {
    return date.day() === 0;
  }

  isSaturday(date) {
    return date.day() === 6
  }

  jumpDate() {
    const currentDate = this.moment(this.currentDate);
    let targetDate = this.moment(this.targetDate);
    if (targetDate.month() !== currentDate.month() || targetDate.year() !== currentDate.year()) {
      this.loadEvent(targetDate).then(events => {
        this.$timeout(() => {
          this.events = events;
          this.scrollToDay(targetDate);
          this.updateGraph(this.events);
          this.csvData = this.CsvUtil.createEventReportCsv(this.events);
          this.downloadFileName = targetDate.format('[event]_YYYY-MM[.csv]');
        });
      });
    } else {
      this.scrollToDay(targetDate);
    }
    this.currentDate = targetDate.toDate();
  }

  changeDate(diff) {
    if (this.autoScrolling) { return; }
    let targetDate = this.moment(this.targetDate);

    targetDate.add(diff, 'days');

    this.targetDate = targetDate.toDate();
    this.currentDate = targetDate.toDate();

    if (scroll) {
      this.scrollToDay(targetDate);
    }
    this.setPlotLine();
  }

  updateEvent(targetDate, direction) {
    return new Promise((resolve, reject) => {
      this.loadEvent(targetDate).then(events => {
        let newEvents = [];
        let newFilename = '';
        if (direction > 0) {
          const threshold = this.moment(this.targetDate).startOf('month');
          newEvents = this.behindEvents(this.events, threshold).concat(events);
          newFilename = this.moment(this.targetDate).format('[event]_YYYY-MM[.csv]');
        } else {
          const threshold = this.moment(this.targetDate).startOf('month').add(1, 'months');
          newEvents = events.concat(this.frontEvents(this.events, threshold));
          newFilename = this.moment(targetDate).format('[event]_YYYY-MM[.csv]');
        }

        this.$timeout(() => {
          this.events = newEvents;
          resolve();
          this.updateGraph(this.events);
          this.csvData = this.CsvUtil.createEventReportCsv(this.events);
          this.downloadFileName = newFilename;
        });
      });
    });
  }

  updateGraph(events) {
    const [normalData, errorData] = this.createGraphData(events);
    this.chart.series[0].setData(normalData);
    this.chart.series[1].setData(errorData);
    this.chart.reflow();
  }

  behindEvents(events, date) {
    let index = this.findIndexByDate(events, date);
    if (index < 1) {
      return events;
    }
    return events.slice(index);
  }

  frontEvents(events, date) {
    let index = this.findIndexByDate(events, date);
    if (index < 1) {
      return events;
    }
    return events.slice(0, index);
  }

  findIndexByDate(events, date) {
    let index = -1;
    for (let [i, event] of events.entries()) {
      if (event.date.valueOf() === date.valueOf()) {
        index = i;
      }
    }

    return index;
  }

  scrollToDay(date, delay = 0, duration = 500) {
    this.autoScrolling = true;
    const index = this.findIndexByDate(this.events, date) + 1;

    this.$timeout(() => {
      this.scrollElement.scrollTo(this.scrollItemSize * (index - 1), 0, duration)
        .then(() => {
          this.autoScrolling = false;
          this.infiniteScroll();
        });
    }, delay);
    this.setPlotLine();
  }

  infiniteScroll() {
    if (this.dragging) {
      return;
    }
    if (this.scrollElement.scrollLeft() < this.loadThreshold) {
      const date = this.moment(this.targetDate).subtract(1, 'month');
      this.autoScrolling = true;
      this.updateEvent(date, -1).then(() => {
        this.scrollToDay(this.targetDate, 0, 0);
      });
    }

    if (this.scrollElement.scrollLeft() > this.scrollElement[0].scrollWidth - this.loadThreshold) {
      const date = this.moment(this.targetDate).add(1, 'month');
      this.autoScrolling = true;
      this.updateEvent(date, 1).then(() => {
        this.scrollToDay(this.targetDate, 0, 0);
      });
    }
  }

  setDragging() {
    this.dragging = true;
  }

  removeDragging() {
    this.dragging = false;
    this.infiniteScroll();
  }

  createGraphData(events) {
    const normalData = [];
    const errorData = [];

    for (let dayEvents of events) {
      const date = dayEvents.date.valueOf();
      normalData.push({
        x: date,
        y: this.$filter('normalEventCount')(dayEvents.events)
      });
      errorData.push({
        x: date,
        y: this.$filter('errorEventCount')(dayEvents.events)
      });
    }

    return [normalData, errorData];
  }

  setPlotLine() {
    this.chart.xAxis[0].removePlotLine('target-date');
    this.chart.xAxis[0].addPlotLine({
      id: 'target-date',
      value: this.targetDate.valueOf(),
      color: '#c1c1c1',
      width: 2,
      zIndex: 0
    });
    this.chart.redraw();
  }

  divideEvents(events, targetDate) {
    const result = [];
    const startDate = targetDate.clone().startOf('month');
    const endDate = startDate.clone().endOf('month');

    for (let date = startDate; date.isBefore(endDate); date.add(1, 'days')) {
      result.push({
        date: date.clone(),
        events: []
      });
    }

    for (let event of events) {
      const occurredAt = this.moment(event.occurred_at).startOf('day');
      let target = result.find(element => {
        return element.date.valueOf() === occurredAt.valueOf();
      });

      target.events.push(event);
    }

    return result;
  }
}

export default angular.module('leptonApp.eventReport', [uiRouter, moment, duScroll, EventService, CsvUtil])
  .config(routing)
  .component('eventReport', {
    require: {parentComponent: '^demandReportShow'},
    template: require('./event_report.html'),
    controller: EventReportController
  })
  .name;



// WEBPACK FOOTER //
// ./public/dev/app/event_report/event_report.component.js